/**
 * Fleet location markers from public.site_locations.
 * Row shape: { id, user_id, domicile, address, latitude, longitude }
 * Marker shape: { id, username, site, address, city, position?, popupText }
 */
import { fetchSiteLocationsByUserIds } from "api/masteraccessapi";
import { parseCityStateForAddresses } from "utils/geocodeAddress";

function cleanAddress(value) {
  const address = String(value ?? "").trim();
  if (!address || address === "#ERROR!") return "";
  return address;
}

function toPosition(latitude, longitude) {
  if (latitude == null || longitude == null) return undefined;
  const latRaw = String(latitude).trim();
  const lngRaw = String(longitude).trim();
  if (latRaw === "" || lngRaw === "") return undefined;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  return [lat, lng];
}

/** Map a site_locations row into the marker shape used by the table and map. */
export function normalizeSiteLocationToMarker(
  row,
  userIdToUsername = {},
  index = 0,
) {
  const username = String(
    userIdToUsername[row?.user_id] ?? row?.username ?? "",
  ).trim();
  const site = String(row?.domicile ?? row?.site ?? "").trim();
  const address = cleanAddress(row?.address);
  const position = toPosition(row?.latitude, row?.longitude);
  const popupText = address ? `${site} — ${address}` : site;

  return {
    id: row?.id ?? `${row?.user_id ?? ""}:${site}:${index}`,
    username,
    site,
    address,
    city: "",
    ...(position ? { position } : {}),
    popupText,
  };
}

/**
 * Fetch and normalize fleet location markers from site_locations.
 * @param {string[]} userIds
 * @param {Record<string, string>} userIdToUsername
 */
export async function fetchFleetLocationMarkers(
  userIds,
  userIdToUsername = {},
) {
  const { data, error } = await fetchSiteLocationsByUserIds(userIds);
  if (error) {
    throw new Error(
      typeof error === "string"
        ? error
        : error?.message || "Failed to load fleet locations.",
    );
  }

  const markers = (data ?? []).map((row, index) =>
    normalizeSiteLocationToMarker(row, userIdToUsername, index),
  );
  let cityByAddress = new Map();
  try {
    cityByAddress = await parseCityStateForAddresses(
      markers.map((marker) => marker.address),
    );
  } catch (err) {
    console.error("Failed to parse city/state from Geocodio:", err);
  }
  return markers.map((marker) => ({
    ...marker,
    city: cityByAddress.get(marker.address) || "",
  }));
}
